// src/pages/UserProfile.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export default function UserProfile() {
  const { id } = useParams(); // loan_id coming from route

  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // ref for printable area
  const printableRef = useRef(null);

  // Example local logo file present in your environment (change if needed)
  const logoLocalPath = '/mnt/data/c5afb376-78f6-4dcb-bbdd-ab1bb0d56157.png';

  // ---- Fetch real API data ----
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setError("");

      try {
        const form = new FormData();
        form.append("action", "loan_status_check");
        form.append("loan_id", id);

        const resp = await axios.post(
          "https://loandestination.in/system/api/web-system/loan.php",
          form
        );

        if (resp?.data?.status === true) {
          const d = resp.data.data;

          setData({
            applicantName: d.name,
            appliedOn: d.last_update,
            applicationId: d.loan_id,
            loanId: d.loan_id,
            contact: d.phone_num,
            email: d.email || "",
            address: d.address,
            dob: d.dob || "",
            loanType: d.loan_type,
            requestedAmount: d.required_loan,
            status: d.loan_status,
            remark: d.remark,
            disbursed_date: d.disbursed_date,
            bank_finance: d.bank_finance,
            reference_by: d.reference_by
          });

          // map documents
          if (Array.isArray(d.document)) {
            setDocs(
              d.document.map((f, index) => ({
                id: index + 1,
                name: f.name,
                url: f.img,
                file_url: f.img,
              }))
            );
          } else {
            setDocs([]);
          }
        } else {
          setError(resp.data?.message || "Failed to load profile");
        }
      } catch (err) {
        console.error(err);
        setError("Error loading profile");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  // currency formatting
  const fmt = (n) => {
    if (n === undefined || n === null || n === "") return "—";
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(Number(n));
    } catch {
      return n;
    }
  };

  // Print handler: open a new window with only printable content + basic styles, then print
  const handlePrint = () => {
    if (!printableRef.current) {
      alert("Nothing to print.");
      return;
    }

    const printableHTML = printableRef.current.innerHTML;

    // create new window
    const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
    if (!printWindow) {
      alert('Pop-up blocked. Please allow pop-ups for this site to print.');
      return;
    }

    // basic styles to keep layout similar — extend as needed
    const styles = `
      <style>
        body { font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; color: #111827; padding: 20px; }
        .card { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.1); padding: 16px; margin-bottom: 12px; }
        h1,h2,h3,h4 { color: #111827; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .doc-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
        img { max-width:100%; height:auto; display:block; }
        @media print {
          .no-print { display: none !important; }
        }
      </style>
    `;

    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html><head><title>Print - Loan Application ${data?.applicationId || ''}</title>${styles}</head><body>`);
    // header (logo + title)
    printWindow.document.write(`
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;">
        <div style="display:flex;align-items:center;gap:12px;">
          <img src="${logoLocalPath}" alt="logo" style="height:48px;object-fit:contain;" onerror="this.style.display='none'"/>
          <div>
            <div style="font-size:18px;font-weight:700;">Loan Application Details</div>
            <div style="font-size:12px;color:#6b7280;">Loan Destination — Application snapshot & documents</div>
          </div>
        </div>
        <div style="text-align:right;font-size:12px;color:#374151;">
          Printed: ${new Date().toLocaleString()}
        </div>
      </div>
    `);

    printWindow.document.write(`<div>${printableHTML}</div>`);
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    // wait a short moment for images to load, then print
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // optionally close window after print:
      // printWindow.close();
    }, 500);
  };

  // Export PDF handler: uses html2canvas + jspdf (dynamic import)
  const handleExportPdf = async () => {
    if (!printableRef.current) {
      alert("Nothing to export.");
      return;
    }

    try {
      // dynamic import so project doesn't break if libs not installed
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      const element = printableRef.current;

      // increase scale for higher quality
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, allowTaint: true });
      const imgData = canvas.toDataURL('image/png');

      // create pdf (A4 portrait)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // scale image to fit width
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth - 40; // 20pt margin each side
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let position = 20;

      // If content height > pageHeight, we need to split into pages
      if (imgHeight <= pageHeight - 40) {
        pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      } else {
        // slice canvas into pages
        const totalPDFPages = Math.ceil(imgHeight / (pageHeight - 40));
        let renderedHeight = 0;
        for (let i = 0; i < totalPDFPages; i++) {
          const sourceY = i * (pageHeight - 40) * (canvas.height / imgHeight);
          // create temporary canvas for slice
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = Math.floor((canvas.height / imgHeight) * (pageHeight - 40));
          const ctx = pageCanvas.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, sourceY, canvas.width, pageCanvas.height, 0, 0, pageCanvas.width, pageCanvas.height);
          const pageImgData = pageCanvas.toDataURL('image/png');
          if (i > 0) pdf.addPage();
          pdf.addImage(pageImgData, 'PNG', 20, 20, imgWidth, (pageCanvas.height * imgWidth) / pageCanvas.width);
          renderedHeight += pageCanvas.height;
        }
      }

      const fileName = `application_${data?.applicationId || id}_${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('Export PDF error', err);
      alert('Export to PDF failed. Make sure html2canvas and jspdf are installed.\n\nRun:\n npm install html2canvas jspdf\n\nThen restart your dev server.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">

      {/* HEADER */}
      <div className="bg-blue-800 text-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* small logo (uses the local path) */}
            <img src={logoLocalPath} alt="Loan Destination" className="w-36 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div>
              <h1 className="text-2xl font-semibold">Loan Application Details</h1>
              <p className="text-sm opacity-80">Professional view — application snapshot & documents</p>
            </div>
          </div>

          <div className="flex items-center gap-3 no-print">
            <button
              className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded shadow-sm"
              onClick={handlePrint}
              title="Print details"
            >
              Print
            </button>
            <button
              className="bg-white text-blue-800 px-3 py-2 rounded shadow-sm"
              onClick={handleExportPdf}
              title="Export details to PDF"
            >
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="max-w-7xl mx-auto p-4">
          <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>
        </div>
      )}

      {/* LOADING */}
      {isLoading && (
        <div className="max-w-7xl mx-auto p-10 text-center text-xl font-semibold">
          Loading...
        </div>
      )}

      {!isLoading && data && (
        // printableRef wraps the content we want to print / export
        <div ref={printableRef} className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 -mt-6">

          {/* LEFT SECTION */}
          <div className="lg:col-span-2 space-y-6">

            {/* PROFILE CARD */}
            <div className="bg-white rounded-lg shadow p-6 mt-6 card">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold">{data.applicantName}</h2>
                  <div className="text-sm text-gray-600 mt-1">
                    Applied on: <span className="font-medium">{data.appliedOn}</span>
                    <span className="mx-2">•</span>
                    Apply ID: <span className="font-medium">{data.applicationId}</span>
                  </div>
                </div>

                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold 
                    ${data.status === "Pending" ? "bg-yellow-200 text-yellow-800" : "bg-green-100 text-green-800"}
                  `}>
                    {data.status}
                  </span>
                  <div className="text-xs text-gray-500 mt-2">Loan ID: {data.loanId}</div>
                </div>
              </div>

              <hr className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                <div>
                  <b>Contact:</b> <br /> {data.contact}
                  <br /><br />
                  <b>Email:</b> <br /> {data.email || "—"}
                  <br /><br />
                  <b>Address:</b> <br /> {data.address}
                  <br /><br />
                  <b>Loan Type:</b> <br /> {data.loanType}
                </div>

                <div>
                  <b>Disbursed Date:</b> <br /> {data.disbursed_date || '—'}
                  <br /><br />
                  <b>Requested Amount:</b> <br /> {fmt(data.requestedAmount)}
                  <br /><br />
                  <b>Remark:</b> <br /> {data.remark || "No remarks provided."}
                </div>
              </div>
            </div>

            {/* DOCUMENTS */}
            <div className="bg-white rounded-lg shadow p-6 card">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Uploaded Documents</h3>
                <span className="text-gray-500">{docs.length} files</span>
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 doc-grid">
                {docs.length === 0 && <p>No documents uploaded.</p>}

                {docs.map((file) => (
                  <div key={file.id} className="bg-gray-50 rounded-lg border p-3">
                    <img src={file.url} className="h-32 w-full object-cover rounded" alt={file.name} />
                    <div className="mt-2 font-medium">{file.name}</div>

                    <div className="mt-2 flex gap-2">
                      <a href={file.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 border rounded text-sm">Open</a>
                      <a href={file.url} download className="px-3 py-1 bg-blue-600 text-white rounded text-sm">Download</a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SECTION */}
          <div className="space-y-6">

            {/* APPLICATION SNAPSHOT */}
            <div className="bg-white rounded-lg shadow p-6 mt-6 card">
              <h4 className="font-semibold mb-4">Application Snapshot</h4>
              <div className="text-sm text-gray-500 grid grid-cols-2 gap-y-2">
                <div>Application ID</div><div className="font-medium">{data.applicationId}</div>
                <div>Loan ID</div><div className="font-medium">{data.loanId}</div>
                <div>Applied On</div><div className="font-medium">{data.appliedOn}</div>
                <div>Loan Type</div><div className="font-medium">{data.loanType}</div>
              </div>
            </div>

            {/* SANCTION / STATUS */}
            <div className="bg-white rounded-lg shadow p-6 card">
              <h4 className="font-semibold mb-4">Sanction / Status</h4>

              <b>Status:</b>
              <div className="mt-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold 
                  ${data.status === "Pending" ? "bg-yellow-200 text-yellow-800" : "bg-green-100 text-green-800"}
                `}>
                  {data.status}
                </span>
              </div>

              <b>Requested Amount:</b>
              <div>{fmt(data.requestedAmount)}</div>

              <b className="mt-4 block">Bank Finance:</b>
              <div>{data.bank_finance || '—'}</div>

              <b className="mt-4 block">Reference By:</b>
              <div>{data.reference_by || '—'}</div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
